import {
  type ReactNode,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useControlled } from "../utils";
import { buildTreeModel, type TreeModel, type TreeNodeMeta } from "./treeModel";

export type { TreeModel, TreeNodeMeta } from "./treeModel";

export interface UseTreeProps {
  /**
   * Default expanded nodes (uncontrolled)
   */
  defaultExpanded?: string[];
  /**
   * Expanded nodes (controlled)
   */
  expanded?: string[];
  /**
   * Callback on expanded nodes change
   */
  onExpandedChange?: (event: SyntheticEvent, expanded: string[]) => void;
  /**
   * Default selected nodes (uncontrolled)
   */
  defaultSelected?: string[];
  /**
   * Selected nodes
   */
  selected?: string[];
  /**
   * Callback on selected nodes change
   */
  onSelectionChange?: (event: SyntheticEvent, selected: string[]) => void;
  /**
   * Sets multiselect mode with checkboxes and allows for multiple node selection
   */
  multiselect?: boolean;
  /**
   * Sets tree to disabled state, preventing all interaction
   */
  disabled?: boolean;
  /**
   * Tree children used to build the tree model for traversal an state management
   */
  children?: ReactNode;
}

function arraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }

  return a.every((value, index) => value === b[index]);
}

function expandSelectionWithDescendants(
  selection: string[],
  model: TreeModel,
  disabledIds: Set<string>,
): string[] {
  const expanded = new Set(selection);

  function addDescendants(parentValue: string): void {
    const children = model.childrenOf.get(parentValue) ?? [];
    for (const child of children) {
      if (!disabledIds.has(child)) {
        expanded.add(child);
        addDescendants(child);
      }
    }
  }

  for (const value of selection) {
    addDescendants(value);
  }

  return Array.from(expanded);
}

function expandSelectionUpwards(
  selection: string[],
  model: TreeModel,
  disabledIds: Set<string>,
): string[] {
  const selectedSet = new Set(selection);

  for (const [value, meta] of model.nodes) {
    if (
      meta.hasChildren &&
      !selectedSet.has(value) &&
      !disabledIds.has(value)
    ) {
      const children = model.childrenOf.get(value) ?? [];
      const enabledChildren = children.filter((c) => !disabledIds.has(c));

      if (
        enabledChildren.length > 0 &&
        enabledChildren.every((c) => selectedSet.has(c))
      ) {
        selectedSet.add(value);
      }
    }
  }

  return Array.from(selectedSet);
}

export function useTree(props: UseTreeProps) {
  const {
    defaultExpanded = [],
    expanded: expandedProp,
    onExpandedChange,
    defaultSelected = [],
    selected: selectedProp,
    onSelectionChange,
    multiselect = false,
    disabled = false,
    children,
  } = props;

  const clampedDefaultSelected = multiselect
    ? defaultSelected
    : defaultSelected.slice(0, 1);

  const clampedSelectedProp =
    selectedProp && !multiselect ? selectedProp.slice(0, 1) : selectedProp;

  const treeModel = useMemo(() => buildTreeModel(children), [children]);
  const elementsRef = useRef<Map<string, HTMLElement>>(new Map());
  const suppressUncontrolledSelectionExpansionRef = useRef(false);

  const disabledIdsSet = useMemo(() => {
    const set = new Set<string>();
    for (const [value, meta] of treeModel.nodes) {
      if (meta.disabled) set.add(value);
    }
    return set;
  }, [treeModel]);

  const [expandedArray, setExpandedArray] = useControlled({
    controlled: expandedProp,
    default: defaultExpanded,
    name: "Tree",
    state: "expanded",
  });

  // Convert array to Set for more efficient lookups during rendering and nav
  const expandedState = useMemo(() => new Set(expandedArray), [expandedArray]);

  const expandedDefaultSelected = useMemo(() => {
    if (!multiselect || clampedDefaultSelected.length === 0) {
      return clampedDefaultSelected;
    }

    let expanded = expandSelectionWithDescendants(
      clampedDefaultSelected,
      treeModel,
      disabledIdsSet,
    );

    expanded = expandSelectionUpwards(expanded, treeModel, disabledIdsSet);

    return expanded;
  }, [clampedDefaultSelected, treeModel, disabledIdsSet, multiselect]);

  const [selectedState, setSelectedState, selectedControlled] = useControlled({
    controlled: clampedSelectedProp,
    default: expandedDefaultSelected,
    name: "Tree",
    state: "selected",
  });

  const setVisibleSelectionState = useCallback((selection: string[]) => {
    suppressUncontrolledSelectionExpansionRef.current = true;
    setSelectedState(selection);
  }, []);

  const selectedSet = useMemo(() => new Set(selectedState), [selectedState]);

  const [activeNode, setActiveNode] = useState<string | undefined>(undefined);

  const registerElement = useCallback((value: string, element: HTMLElement) => {
    elementsRef.current.set(value, element);

    return () => {
      if (elementsRef.current.get(value) === element) {
        elementsRef.current.delete(value);
      }
    };
  }, []);

  const getElement = useCallback((value: string): HTMLElement | undefined => {
    return elementsRef.current.get(value);
  }, []);

  const getNodeMeta = useCallback(
    (value: string): TreeNodeMeta | undefined => {
      return treeModel.nodes.get(value);
    },
    [treeModel],
  );

  const getParent = useCallback(
    (value: string): string | undefined => {
      return treeModel.nodes.get(value)?.parentValue;
    },
    [treeModel],
  );

  const getChildren = useCallback(
    (parentValue: string): string[] => {
      return treeModel.childrenOf.get(parentValue) ?? [];
    },
    [treeModel],
  );

  // Depth-first search (with pre-order traversal)
  const getDescendants = useCallback(
    (value: string): string[] => {
      const descendants: string[] = [];

      function traverse(parentValue: string): void {
        const children = treeModel.childrenOf.get(parentValue) ?? [];
        for (const child of children) {
          if (!disabledIdsSet.has(child)) {
            descendants.push(child);
            traverse(child);
          }
        }
      }

      traverse(value);
      return descendants;
    },
    [treeModel, disabledIdsSet],
  );

  const getAncestors = useCallback(
    (value: string): string[] => {
      const ancestors: string[] = [];
      let current = treeModel.nodes.get(value)?.parentValue;

      while (current) {
        ancestors.push(current);
        current = treeModel.nodes.get(current)?.parentValue;
      }

      return ancestors;
    },
    [treeModel],
  );

  const toggleExpanded = useCallback(
    (event: SyntheticEvent, value: string) => {
      const isExpanding = !expandedState.has(value);
      const newExpanded = isExpanding
        ? [...expandedArray, value]
        : expandedArray.filter((v) => v !== value);

      setExpandedArray(newExpanded);
      onExpandedChange?.(event, newExpanded);
    },
    [expandedArray, expandedState, onExpandedChange],
  );

  const calculateIndeterminateState = useCallback(
    (selected: string[]): Set<string> => {
      const indeterminate = new Set<string>();
      const selectedSet = new Set(selected);

      for (const selectedValue of selected) {
        let current = getParent(selectedValue);

        while (current) {
          const children = getChildren(current);
          const enabledChildren = children.filter(
            (child) => !disabledIdsSet.has(child),
          );

          if (enabledChildren.length === 0) {
            current = getParent(current);
            continue;
          }

          const selectedChildren = enabledChildren.filter((child) =>
            selectedSet.has(child),
          );
          const allChildrenSelected =
            selectedChildren.length === enabledChildren.length;
          const someChildrenSelected = selectedChildren.length > 0;

          const someChildrenIndeterminate = enabledChildren.some((child) =>
            indeterminate.has(child),
          );

          if (
            someChildrenIndeterminate ||
            (someChildrenSelected && !allChildrenSelected)
          ) {
            indeterminate.add(current);
          }

          current = getParent(current);
        }
      }

      return indeterminate;
    },
    [getParent, getChildren, disabledIdsSet],
  );

  const indeterminateState = useMemo(() => {
    const state = multiselect
      ? calculateIndeterminateState(selectedState)
      : new Set<string>();
    return state;
  }, [multiselect, selectedState, calculateIndeterminateState]);

  useEffect(() => {
    if (suppressUncontrolledSelectionExpansionRef.current) {
      suppressUncontrolledSelectionExpansionRef.current = false;
      return;
    }

    if (selectedControlled || !multiselect || selectedState.length === 0) {
      return;
    }

    let expanded = expandSelectionWithDescendants(
      selectedState,
      treeModel,
      disabledIdsSet,
    );
    expanded = expandSelectionUpwards(expanded, treeModel, disabledIdsSet);

    if (!arraysEqual(selectedState, expanded)) {
      setSelectedState(expanded);
    }
  }, [
    selectedControlled,
    multiselect,
    selectedState,
    treeModel,
    disabledIdsSet,
  ]);

  const updateAncestors = (
    currentSet: Set<string>,
    value: string,
  ): string[] => {
    const ancestors = getAncestors(value);

    for (const ancestor of ancestors) {
      const children = treeModel.childrenOf.get(ancestor) ?? [];
      const enabledChildren = children.filter(
        (child) => !disabledIdsSet.has(child),
      );

      if (enabledChildren.length === 0) continue;

      const allSelected = enabledChildren.every((child) =>
        currentSet.has(child),
      );

      if (allSelected) {
        currentSet.add(ancestor);
      } else {
        currentSet.delete(ancestor);
      }
    }

    return Array.from(currentSet);
  };

  const getMultiSelectState = (value: string) => {
    const currentSet = new Set(selectedState);
    const descendants = getDescendants(value);

    if (currentSet.has(value)) {
      currentSet.delete(value);
      const descendantSet = new Set(descendants);
      for (const d of descendantSet) {
        currentSet.delete(d);
      }
    } else {
      currentSet.add(value);
      for (const d of descendants) {
        if (!currentSet.has(d)) {
          currentSet.add(d);
        }
      }
    }

    return updateAncestors(currentSet, value);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: getMultiSelectState/updateAncestors are intentionally not memoized - their captured values (selectedState, treeModel, etc.) are already in deps
  const select = useCallback(
    (event: SyntheticEvent, value: string) => {
      if (disabled || disabledIdsSet.has(value)) return;

      let newSelected: string[];

      if (multiselect) {
        newSelected = getMultiSelectState(value);
      } else {
        const isCurrentlySelected = selectedSet.has(value);
        newSelected = isCurrentlySelected ? [] : [value];
      }

      setSelectedState(newSelected);
      onSelectionChange?.(event, newSelected);
    },
    [disabled, disabledIdsSet, multiselect, selectedState, onSelectionChange],
  );

  // Visible nodes in depth-first order matching visual tree order
  const visibleNodes = useMemo((): string[] => {
    const visible: string[] = [];

    function traverse(values: string[]): void {
      for (const value of values) {
        visible.push(value);

        const nodeMeta = treeModel.nodes.get(value);
        if (nodeMeta?.hasChildren && expandedState.has(value)) {
          const children = treeModel.childrenOf.get(value) ?? [];
          traverse(children);
        }
      }
    }

    traverse(treeModel.rootValues);
    return visible;
  }, [treeModel, expandedState]);

  const tabbableNodeId = useMemo((): string | undefined => {
    if (activeNode) {
      return activeNode;
    }

    const firstSelectedVisible = visibleNodes.find((node) =>
      selectedSet.has(node),
    );

    if (firstSelectedVisible !== undefined) {
      return firstSelectedVisible;
    }

    return visibleNodes[0];
  }, [activeNode, selectedSet, visibleNodes]);

  return {
    expandedArray,
    setExpandedArray,
    expandedState,
    toggleExpanded,
    selectedState,
    selectedSet,
    setSelectedState,
    setVisibleSelectionState,
    select,
    multiselect,
    disabled,
    disabledIdsSet,
    treeModel,
    getNodeMeta,
    getParent,
    getChildren,
    getDescendants,
    getAncestors,
    visibleNodes,
    tabbableNodeId,
    registerElement,
    getElement,
    activeNode,
    setActiveNode,
    indeterminateState,
  };
}
